import { useEffect } from "react";

import { useStore } from "@/store";
import { useIsMyDoc } from "@/utils/useIsMyDoc";
import { useIsPublicProfileDoc } from "@/utils/useIsPublicProfileDoc";

import type { ChatModel } from "@/models/chat/ChatModel";
import type { TeamModel } from "@/models/TeamModel";

export type MentionScope = "document" | "team";

export type MentionAccess = {
  scope: MentionScope;
  lacksAccess: boolean;
  team?: TeamModel;
};

/**
 * Whether the mentioned person can see what they were mentioned in.
 *
 * Nothing is claimed until every roster involved is in hand.
 */
export function useMentionAccess(userId: string, chat?: ChatModel): MentionAccess {
  const { doc, teams } = useStore();
  const canShare = useIsMyDoc(doc);
  const isPublished = useIsPublicProfileDoc(doc);

  const chatTeamUid = chat?.accessTeamUid;
  const scope: MentionScope = chatTeamUid ? "team" : "document";
  const chatTeam = chatTeamUid ? teams.userTeams.find(({ uid }) => uid === chatTeamUid) : undefined;

  const openToEveryone = !!doc && (doc.publicRead || doc.publicWrite || isPublished);
  const readsDocument = scope === "document" && !!doc && canShare && !openToEveryone;

  useEffect(() => {
    if (!readsDocument || doc.permissions.loadStatus !== "init") return;
    doc.permissions.fetchPermissionsAndRequests().catch((e) => console.error(e));
  }, [doc, readsDocument]);

  const ownerTeam = doc?.api.team;
  const permissions = doc?.permissions.permissions;
  const permissionsLoaded = doc?.permissions.loadStatus === "loaded";

  function collectHolders() {
    if (scope === "team") return chatTeam ? [chatTeam] : [];
    if (!readsDocument || !permissionsLoaded || !ownerTeam || !permissions) return [];

    const byUid = new Map([[ownerTeam.uid, ownerTeam]]);
    for (const { team, confirmed } of permissions) {
      if (confirmed && team) byUid.set(team.uid, team);
    }
    return Array.from(byUid.values());
  }

  const holders = collectHolders();

  useEffect(() => {
    for (const { uid } of holders) {
      const own = teams.userTeams.find((team) => team.uid === uid);
      if (own?.membersUX.membersStatus === "init") {
        own.membersUX.loadMembers().catch((e) => console.error(e));
      }
    }
  }, [holders, teams]);

  const audience = new Set<string>();
  let complete = holders.length > 0;
  for (const { uid } of holders) {
    const own = teams.userTeams.find((team) => team.uid === uid);
    const shared = permissions?.find(({ team }) => team?.uid === uid)?.team;
    const roster = own
      ? own.membersUX.membersStatus === "loaded" &&
        own.membersUX.members.map(({ user }) => user.uid)
      : shared?.ownerUid && [shared.ownerUid];

    if (!roster) {
      complete = false;
      break;
    }
    for (const memberUid of roster) audience.add(memberUid);
  }

  return {
    scope,
    lacksAccess: complete && !audience.has(userId),
    team: chatTeam?.userPermissions?.invite ? chatTeam : undefined,
  };
}
